import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS } from '../designSystem';
import { hasPermission, PERMISSIONS } from '../authUtils';
import { usePageHeader } from '../contexts/PageHeaderContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const StatCard = ({ value, label, trend = null }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
  <div
    style={{
      backgroundColor: COLORS.BG_SURFACE_PRIMARY,
      border: `1px solid ${hovered ? 'rgba(77, 219, 148, 0.2)' : COLORS.BORDER_DEFAULT}`,
      borderRadius: RADIUS.DEFAULT,
      padding: SPACING.LG,
      boxShadow: hovered ? SHADOWS.MD : SHADOWS.MD,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'default',
    }}
    onMouseEnter={() => setHovered(true)}
    onMouseLeave={() => setHovered(false)}
  >
    <div style={{
      ...TYPOGRAPHY.BODY_SM,
      color: COLORS.TEXT_SECONDARY,
      marginBottom: SPACING.SM,
    }}>
      {label}
    </div>
    <div style={{
      fontSize: '28px',
      fontWeight: '700',
      color: COLORS.TEXT_PRIMARY,
      marginBottom: SPACING.SM,
    }}>
      {value}
    </div>
      {trend && (
        <div style={{
          ...TYPOGRAPHY.BODY_SM,
          color: trend.value > 0 ? COLORS.SUCCESS : COLORS.ERROR,
        }}>
          {trend.value > 0 ? '↑' : '↓'} {Math.abs(trend.value)}% {trend.label}
        </div>
      )}
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { setHeaderConfig, resetHeaderConfig } = usePageHeader();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({ totalMerchants: 0, totalDeals: 0, activeDeals: 0 });
  const [recentDeals, setRecentDeals] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const [userRes, merchantsRes, dealsRes] = await Promise.all([
          axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/merchants`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/deals`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setUser(userRes.data.user);

        const totalMerchants = merchantsRes.data?.length || 0;
        const dealsData = dealsRes.data?.deals || dealsRes.data || [];
        const totalDeals = dealsData.length || 0;
        const activeDeals = dealsData.filter(d => d.status === 'active' || d.status === 'funded').length;

        setStats({ totalMerchants, totalDeals, activeDeals });
        setRecentDeals(dealsData.slice(0, 5));
      } catch (err) {
        setError('Failed to load dashboard');
        setTimeout(() => {
          localStorage.removeItem('token');
          navigate('/login');
        }, 2000);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [navigate]);

  // Set up unified header
  useEffect(() => {
    setHeaderConfig({
      title: 'Dashboard',
      subtitle: user ? `Welcome back, ${user.email}` : 'Welcome back',
      actions: [
        hasPermission(PERMISSIONS.MERCHANT_CREATE) && (
          <Link key="merchant" to="/merchants/new" style={{
            padding: `${SPACING.SM} ${SPACING.LG}`,
            backgroundColor: COLORS.PRIMARY_DARK,
            color: COLORS.TEXT_WHITE,
            border: 'none',
            borderRadius: RADIUS.DEFAULT,
            fontSize: TYPOGRAPHY.BODY.fontSize,
            fontWeight: '600',
            cursor: 'pointer',
            textDecoration: 'none',
            display: 'inline-block',
            transition: 'all 0.2s ease',
          }}>
            + New Merchant
          </Link>
        ),
        hasPermission(PERMISSIONS.DEAL_CREATE) && (
          <Link key="deal" to="/deals/new" style={{
            padding: `${SPACING.SM} ${SPACING.LG}`,
            backgroundColor: COLORS.PRIMARY_DARK,
            color: COLORS.TEXT_WHITE,
            border: 'none',
            borderRadius: RADIUS.DEFAULT,
            fontSize: TYPOGRAPHY.BODY.fontSize,
            fontWeight: '600',
            cursor: 'pointer',
            textDecoration: 'none',
            display: 'inline-block',
            transition: 'all 0.2s ease',
          }}>
            + New Advance
          </Link>
        ),
      ].filter(Boolean),
    });

    // Cleanup: reset header when unmounting
    return () => resetHeaderConfig();
  }, [user, setHeaderConfig, resetHeaderConfig]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: SPACING.XXXL, color: COLORS.TEXT_LIGHT }}>
          Loading dashboard...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={{ padding: SPACING.XL, backgroundColor: COLORS.ERROR, color: 'white', borderRadius: RADIUS.DEFAULT }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageWrapper}>
      <div style={styles.container}>
        {/* Stat Cards Row */}
      <div style={styles.statsRow}>
        <StatCard value={stats.totalMerchants} label="Total Merchants" />
        <StatCard value={stats.totalDeals} label="Total Advances" />
        <StatCard value={stats.activeDeals} label="Active Advances" />
      </div>

      {/* Chart Placeholder */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>Funding Trend (Last 6 Months)</h3>
        </div>
        <div style={{
          height: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: COLORS.BG_SURFACE_SECONDARY,
          borderRadius: RADIUS.DEFAULT,
          color: COLORS.TEXT_LIGHT,
        }}>
          <p style={{...TYPOGRAPHY.BODY, marginTop: 0}}>Chart coming soon</p>
        </div>
      </div>

      {/* Two-Column Section */}
      <div style={styles.twoColumnRow}>
        {/* Recent Advances */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Recent Advances</h3>
            <Link to="/deals" style={{...TYPOGRAPHY.BODY_SM, color: COLORS.PRIMARY_DARK, textDecoration: 'none'}}>
              View all →
            </Link>
          </div>
          {recentDeals.length === 0 ? (
            <div style={{ padding: SPACING.XL, textAlign: 'center', color: COLORS.TEXT_LIGHT }}>
              <p style={TYPOGRAPHY.BODY_SM}>No advances yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.MD }}>
              {recentDeals.map((deal) => (
                <div
                  key={deal.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: SPACING.MD,
                    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                  }}
                  onClick={() => navigate(`/deals/${deal.id}`)}
                >
                  <div>
                    <div style={{...TYPOGRAPHY.BODY, fontWeight: '600', color: COLORS.TEXT_PRIMARY}}>
                      {deal.id}
                    </div>
                    <div style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_SECONDARY}}>
                      {deal.merchant_name || 'Unknown'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{...TYPOGRAPHY.BODY, fontWeight: '600', color: COLORS.TEXT_PRIMARY}}>
                      ${deal.amount ? deal.amount.toLocaleString() : '0'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Quick Links</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.MD }}>
            <Link
              to="/merchants"
              style={styles.actionButtonGhost}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(77, 219, 148, 0.08)';
                e.currentTarget.style.borderColor = COLORS.PRIMARY;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = COLORS.BORDER_LIGHT;
              }}
            >
              View All Merchants →
            </Link>
            <Link
              to="/deals"
              style={styles.actionButtonGhost}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(77, 219, 148, 0.08)';
                e.currentTarget.style.borderColor = COLORS.PRIMARY;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = COLORS.BORDER_LIGHT;
              }}
            >
              View All Advances →
            </Link>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

const styles = {
  pageWrapper: {
    backgroundColor: COLORS.BG_PAGE_SURFACE,
    minHeight: '100vh',
  },

  container: {
    padding: `${SPACING.MD} ${SPACING.XL} ${SPACING.XXL} ${SPACING.XL}`,
    maxWidth: '1400px',
    margin: '0 auto',
  },

  primaryButton: {
    padding: `${SPACING.MD} ${SPACING.XL}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    textDecoration: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontWeight: '700',
    fontSize: TYPOGRAPHY.BODY.fontSize,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'inline-block',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(77, 219, 148, 0.3)',
    letterSpacing: '0.3px',
    whiteSpace: 'nowrap',
  },

  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: SPACING.XL,
    marginBottom: SPACING.XXXL,
  },

  card: {
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    border: `1px solid ${COLORS.BORDER_DEFAULT}`,
    borderRadius: RADIUS.DEFAULT,
    padding: SPACING.LG,
    boxShadow: SHADOWS.MD,
    marginBottom: SPACING.XXXL,
    transition: 'all 0.2s ease',
  },

  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACING.XL,
    paddingBottom: SPACING.LG,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
  },

  cardTitle: {
    ...TYPOGRAPHY.H3,
    color: COLORS.TEXT_PRIMARY,
    margin: 0,
  },

  twoColumnRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: SPACING.XL,
  },

  actionButton: {
    display: 'flex',
    alignItems: 'center',
    padding: `${SPACING.MD} ${SPACING.XL}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    textDecoration: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontWeight: '700',
    fontSize: TYPOGRAPHY.BODY.fontSize,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(77, 219, 148, 0.3)',
    letterSpacing: '0.3px',
  },

  actionButtonGhost: {
    display: 'flex',
    alignItems: 'center',
    padding: `${SPACING.MD} ${SPACING.LG}`,
    backgroundColor: 'transparent',
    color: COLORS.PRIMARY_DARK,
    textDecoration: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontWeight: '600',
    fontSize: TYPOGRAPHY.BODY.fontSize,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
  },
};
